import { useEffect, useState } from "react";
import { Clock, Thermometer } from "lucide-react";
import HelpTooltip from "@/shared/components/HelpTooltip";

const SP_LAT = -23.5505;
const SP_LON = -46.6333;
const WEATHER_CACHE_KEY = "fg_header_weather_temp_c";

type Coords = { lat: number; lon: number };

function readCachedTempC(): number | null {
  try {
    const raw = sessionStorage.getItem(WEATHER_CACHE_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeCachedTempC(t: number) {
  try {
    sessionStorage.setItem(WEATHER_CACHE_KEY, String(t));
  } catch {
    /* quota / modo privado */
  }
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

async function fetchCurrentTempC(lat: number, lon: number): Promise<number | null> {
  try {
    const params = new URLSearchParams({
      latitude: String(lat),
      longitude: String(lon),
      current: "temperature_2m",
      timezone: "America/Sao_Paulo",
    });
    const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params.toString()}`, {
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return readCachedTempC();
    const json = await res.json();
    const t = json?.current?.temperature_2m;
    if (typeof t === "number" && Number.isFinite(t)) {
      const rounded = Math.round(t * 10) / 10;
      writeCachedTempC(rounded);
      return rounded;
    }
    return null;
  } catch {
    return readCachedTempC();
  }
}

type HeaderClockWeatherProps = {
  className?: string;
};

/**
 * Hora local (atualização periódica) e temperatura atual (Open-Meteo).
 * Geolocalização opcional; se indisponível, usa referência São Paulo.
 */
export default function HeaderClockWeather({ className = "" }: HeaderClockWeatherProps) {
  const [now, setNow] = useState(() => new Date());
  /** Referência SP imediata — evita esperar geolocalização (até 12s) antes da primeira temperatura. */
  const [coords, setCoords] = useState<Coords>({ lat: SP_LAT, lon: SP_LON });
  const [tempC, setTempC] = useState<number | null>(() => readCachedTempC());
  const [tempLoading, setTempLoading] = useState(true);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
      },
      () => {},
      { enableHighAccuracy: false, maximumAge: 600_000, timeout: 12_000 }
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTempLoading(true);
    fetchCurrentTempC(coords.lat, coords.lon)
      .then((t) => {
        if (!cancelled) setTempC(t);
      })
      .catch(() => {
        if (!cancelled) setTempC(null);
      })
      .finally(() => {
        if (!cancelled) setTempLoading(false);
      });
    const refresh = window.setInterval(() => {
      fetchCurrentTempC(coords.lat, coords.lon)
        .then((t) => {
          if (!cancelled && t != null) setTempC(t);
        })
        .catch(() => {});
    }, 30 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(refresh);
    };
  }, [coords]);

  const tempLabel =
    tempLoading && tempC == null ? "…" : tempC != null ? `${tempC}°C` : "—";

  return (
    <div
      className={`hidden sm:flex items-center gap-3 min-w-0 shrink text-muted-foreground ${className}`}
      aria-live="polite"
    >
      <span
        className="inline-flex items-center gap-1.5 tabular-nums text-foreground/90"
        title="Hora local do dispositivo"
      >
        <Clock className="h-3.5 w-3.5 shrink-0 text-[#791614]/80" aria-hidden />
        <span className="font-accent text-[11px] uppercase tracking-widest">{formatTime(now)}</span>
      </span>
      <span className="h-4 w-px bg-border/50 shrink-0" aria-hidden />
      <span
        className="inline-flex items-center gap-1.5 tabular-nums text-foreground/90"
        title="Temperatura ao ar livre (previsão Open-Meteo)"
      >
        <Thermometer className="h-3.5 w-3.5 shrink-0 text-[#d7b369]" aria-hidden />
        <span className="font-accent text-[11px] uppercase tracking-widest">{tempLabel}</span>
        <HelpTooltip text="Hora: relógio local do seu navegador, atualizada a cada 15 segundos.\n\nTemperatura: valor atual ao ar livre (API Open-Meteo). Com permissão de localização usa sua posição; caso contrário, referência na região de São Paulo. Atualização automática a cada 30 minutos." />
      </span>
    </div>
  );
}
